/**
 * Centralized auth redirect logic - single source of truth.
 * Use these constants and helpers instead of hardcoding paths.
 */

import { redirect } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/auth-store';

// ─── Scenario-based redirect targets ────────────────────────────────────────

/** Where to redirect users in each scenario */
export const REDIRECT = {
  /** After successful logout */
  postLogout: '/login',
  /** After login when no ?redirect= param (also: authenticated user visiting /login, root redirect) */
  postLogin: '/start',
  /** Login page path (for building URLs, unauthenticated redirect) */
  login: '/login',
} as const;

/** Role-based path mapping: company_role_id -> route */
const ROLE_PATH: Record<number, string> = {
  1: '/owner/overview', // L1
  2: '/company', // L2
  3: '/employee', // L3
};

/**
 * Get role-based redirect path from company_role_id.
 */
export function getRoleBasedPath(companyRoleId: number): string {
  return ROLE_PATH[companyRoleId] ?? '/employee';
}

// ─── Redirect helpers ───────────────────────────────────────────────────────

/**
 * Get post-login target. Uses ?redirect= search param if present, else postLoginDefault.
 */
export function getPostLoginRedirect(): string {
  if (typeof window === 'undefined') return REDIRECT.postLogin;
  const redirectTo = new URLSearchParams(window.location.search).get('redirect');
  return redirectTo || REDIRECT.postLogin;
}

/**
 * Build login URL with optional ?redirect= param for return-after-login.
 */
export function getLoginUrl(redirectTo?: string): string {
  if (!redirectTo) return REDIRECT.login;
  const params = new URLSearchParams({ redirect: redirectTo });
  return `${REDIRECT.login}?${params.toString()}`;
}

// ─── Route guards (use in beforeLoad) ────────────────────────────────────────

/** Redirect unauthenticated users to login. Use on protected app routes. */
export function redirectIfUnauthenticated({
  location,
}: {
  location: { href: string };
}) {
  const { token, user } = useAuthStore.getState();

  if (!token || !user) {
    throw redirect({
      to: REDIRECT.login,
      search: { redirect: location.href },
    });
  }
}

/** Redirect authenticated users away from login. Use on auth routes. */
export function redirectIfAuthenticated() {
  const { token, user } = useAuthStore.getState();

  if (token && user) {
    throw redirect({
      to: REDIRECT.postLogin,
    });
  }
}
